import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Trash2, FileText, MapPin } from 'lucide-react'
import api from '@/lib/axios'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { formatDateTime } from '@/lib/utils'

interface AnnotationWithDetails {
  id: string
  document_id: string
  document_name: string | null
  page_number: number
  field_id: string | null
  field_name: string | null
  bounding_box: { x: number; y: number; width: number; height: number } | null
  selected_text: string | null
  corrected_value: string | null
  annotation_note: string | null
  is_active: boolean
  created_by: string
  created_at: string
}

interface Props {
  projectId: string
}

export default function AnnotationsList({ projectId }: Props) {
  const queryClient = useQueryClient()

  const { data: annotations = [], isLoading } = useQuery({
    queryKey: ['project-annotations', projectId],
    queryFn: () =>
      api.get<AnnotationWithDetails[]>(`/annotations/project/${projectId}`).then((r) => r.data),
  })

  const deleteMutation = useMutation({
    mutationFn: (id: string) => api.delete(`/annotations/${id}`).then((r) => r.data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['project-annotations', projectId] })
      queryClient.invalidateQueries({ queryKey: ['annotations'] })
      queryClient.invalidateQueries({ queryKey: ['documents', projectId] })
    },
  })

  const toggleMutation = useMutation({
    mutationFn: (id: string) => api.patch(`/annotations/${id}/toggle`).then((r) => r.data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['project-annotations', projectId] })
    },
  })

  // Group by document
  const byDocument: Record<string, AnnotationWithDetails[]> = {}
  for (const ann of annotations) {
    const key = ann.document_id
    if (!byDocument[key]) byDocument[key] = []
    byDocument[key].push(ann)
  }

  if (isLoading) {
    return <div className="text-center py-8 text-muted-foreground">Loading annotations...</div>
  }

  if (annotations.length === 0) {
    return (
      <div className="text-center py-12 border-2 border-dashed rounded-lg">
        <MapPin className="h-8 w-8 text-muted-foreground mx-auto mb-3" />
        <p className="text-muted-foreground">No annotations yet</p>
        <p className="text-sm text-muted-foreground mt-1">
          Open a document and use the Annotate tool to draw bounding boxes on fields.
          Annotations serve as few-shot examples to improve extraction accuracy.
        </p>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-lg font-semibold">Annotations</h2>
        <p className="text-sm text-muted-foreground">
          {annotations.length} annotation{annotations.length !== 1 ? 's' : ''} across {Object.keys(byDocument).length} document{Object.keys(byDocument).length !== 1 ? 's' : ''}.
          {' '}<span className="text-green-600">{annotations.filter(a => a.is_active).length} active</span>
          {annotations.some(a => !a.is_active) && <span className="text-muted-foreground"> · {annotations.filter(a => !a.is_active).length} disabled</span>}
          These are used as few-shot examples during extraction to improve accuracy.
        </p>
      </div>

      {Object.entries(byDocument).map(([docId, docAnnotations]) => {
        const docName = docAnnotations[0]?.document_name || docId
        return (
          <div key={docId} className="border rounded-lg overflow-hidden">
            <div className="bg-muted/50 px-4 py-2 flex items-center gap-2 border-b">
              <FileText className="h-4 w-4 text-muted-foreground" />
              <span className="font-medium text-sm">{docName}</span>
              <Badge variant="outline" className="text-xs ml-auto">
                {docAnnotations.length} annotation{docAnnotations.length !== 1 ? 's' : ''}
              </Badge>
            </div>
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b text-muted-foreground">
                  <th className="text-center p-3 font-medium w-16">Active</th>
                  <th className="text-left p-3 font-medium">Field</th>
                  <th className="text-left p-3 font-medium">Page</th>
                  <th className="text-left p-3 font-medium">Raw Text</th>
                  <th className="text-left p-3 font-medium">Correct Extraction</th>
                  <th className="text-left p-3 font-medium">Note</th>
                  <th className="text-left p-3 font-medium">Created</th>
                  <th className="text-right p-3 font-medium w-16"></th>
                </tr>
              </thead>
              <tbody>
                {docAnnotations.map((ann) => (
                  <tr key={ann.id} className={`border-b last:border-0 hover:bg-muted/20 ${!ann.is_active ? 'opacity-50' : ''}`}>
                    <td className="p-3 text-center">
                      <input
                        type="checkbox"
                        checked={ann.is_active}
                        onChange={() => toggleMutation.mutate(ann.id)}
                        disabled={toggleMutation.isPending}
                        title={ann.is_active ? 'Disable — exclude from extraction' : 'Enable — include in extraction'}
                      />
                    </td>
                    <td className="p-3">
                      {ann.field_name ? (
                        <Badge variant="secondary" className="text-xs">{ann.field_name}</Badge>
                      ) : (
                        <span className="text-muted-foreground text-xs">Unassigned</span>
                      )}
                    </td>
                    <td className="p-3 text-muted-foreground">p.{ann.page_number}</td>
                    <td className="p-3 max-w-[200px]">
                      {ann.selected_text ? (
                        <span className="truncate block text-muted-foreground" title={ann.selected_text}>
                          "{ann.selected_text}"
                        </span>
                      ) : (
                        <span className="text-muted-foreground">—</span>
                      )}
                    </td>
                    <td className="p-3 max-w-[200px]">
                      {ann.corrected_value ? (
                        <span className="truncate block font-medium text-green-700" title={ann.corrected_value}>
                          {ann.corrected_value}
                        </span>
                      ) : (
                        <span className="text-muted-foreground">—</span>
                      )}
                    </td>
                    <td className="p-3 max-w-[150px] text-muted-foreground">
                      {ann.annotation_note ? (
                        <span className="truncate block" title={ann.annotation_note}>
                          {ann.annotation_note}
                        </span>
                      ) : '—'}
                    </td>
                    <td className="p-3 text-muted-foreground text-xs whitespace-nowrap">
                      {formatDateTime(ann.created_at)}
                    </td>
                    <td className="p-3 text-right">
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-7 w-7"
                        onClick={() => {
                          if (confirm('Delete this annotation?')) {
                            deleteMutation.mutate(ann.id)
                          }
                        }}
                        disabled={deleteMutation.isPending}
                      >
                        <Trash2 className="h-3.5 w-3.5 text-destructive" />
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )
      })}
    </div>
  )
}
